import { useState } from 'react';
import {
  BLOCK_PALETTES,
  renderText,
  tokensWithoutFallback,
  validateBlocks,
  type Block,
  type BlockDocument,
  type BlockPalette,
  type BlockType,
} from '@whinerp/contracts/blocks';
import { cx } from '../primitives/cx.js';
import { passthrough } from '../primitives/passthrough.js';
import { Button } from '../primitives/Button.tsx';
import { Icon } from '../primitives/Icon.tsx';
import { SectionLabel } from '../primitives/Surface.tsx';

/**
 * BlockEditor - one editor, three surfaces.
 *
 * An email, a landing page and a knowledge article are the same block document
 * with a different palette. The palette is a prop; there is no email editor and
 * no article editor. Three components would be three places for a block to
 * gain a field the other two cannot read, and portability would quietly stop
 * being true while the type still said it was.
 *
 * Three things it insists on.
 *
 * **The preview shows what a recipient would get, including what is missing.**
 * A token with no value renders as its own name in braces rather than as
 * nothing, and every such token is listed beside the canvas. "Hi ," is the
 * most recognisable way to tell somebody they are a row in a spreadsheet, and
 * it survives review precisely because a preview that renders nothing looks
 * perfect.
 *
 * **The palette offers only what this surface can carry.** A control offering
 * a block the document will be refused for is a control that teaches distrust.
 *
 * **Problems are shown against the block, not at the top.** A list of eleven
 * problems above a canvas of twenty blocks is a puzzle.
 */

export type PreviewMode = 'desktop' | 'mobile' | 'dark';

export interface BlockEditorLabels {
  palette: string;
  canvas: string;
  settings: string;
  addBlock: (type: string) => string;
  blockType: Record<BlockType, string>;
  moveUp: string;
  moveDown: string;
  remove: string;
  /** Field labels in the settings pane. */
  text: string;
  url: string;
  alt: string;
  href: string;
  align: string;
  alignOptions: Record<'start' | 'center' | 'end', string>;
  /** Preview controls. */
  preview: string;
  previewMode: Record<PreviewMode, string>;
  /** The personalisation warning, which is the point of the preview. */
  missingTokens: string;
  missingTokensHelp: string;
  /** Shown on an empty canvas. */
  empty: string;
  /** The document-level problem list, for problems that belong to no block. */
  problems: string;
  selectBlock: string;
  testSend: string;
}

export interface BlockEditorProps {
  palette: BlockPalette;
  value: BlockDocument;
  onChange: (next: BlockDocument) => void;
  labels: BlockEditorLabels;
  /**
   * A real record's values, for the preview.
   *
   * Passed in rather than fetched: the editor previews against whatever the
   * caller considers representative, and an email builder's idea of a sample
   * contact is not this component's business.
   */
  sample?: Record<string, string | null | undefined>;
  /** Renders the test-send control. Absent means this surface does not send. */
  onTestSend?: (() => void) | undefined;
  readOnly?: boolean;
  /**
   * The asset library (F-084), when this workspace has it: images to choose
   * for an image block, and snippets and templates to insert. Passed in, like
   * the sample, so the editor never decides what a library is.
   */
  library?: BlockLibrary | undefined;
}

export interface BlockLibrary {
  label: string;
  chooseImage: string;
  images: Array<{ name: string; url: string }>;
  snippets: Array<{ name: string; body: string }>;
  templates: Array<{ name: string; blocks: Block[] }>;
}

/** A template's blocks with ids of their own, so inserting it twice is two sets of blocks. */
function freshIds(blocks: Block[]): Block[] {
  return blocks.map((block) => ({
    ...block,
    id: `b${Math.random().toString(36).slice(2, 9)}`,
    ...(block.children ? { children: block.children.map((column) => freshIds(column)) } : {}),
  }));
}

/** A new block of a type, with the fields that type needs already present. */
function newBlock(type: BlockType): Block {
  const id = `b${Math.random().toString(36).slice(2, 9)}`;
  switch (type) {
    case 'columns':
      return { id, type, children: [[], []] };
    case 'button':
      return { id, type, text: '', href: '', align: 'center' };
    case 'image':
      return { id, type, url: '', alt: '' };
    case 'unsubscribe':
      return { id, type, text: '' };
    default:
      return { id, type, text: '' };
  }
}

export function BlockEditor({
  palette,
  value,
  onChange,
  labels,
  sample = {},
  onTestSend,
  readOnly,
  library,
  ...rest
}: BlockEditorProps & Record<string, unknown>): JSX.Element {
  const [selected, setSelected] = useState<string | null>(null);
  const [mode, setMode] = useState<PreviewMode>('desktop');

  const problems = validateBlocks(value, palette);
  const missing = tokensWithoutFallback(value, sample);
  const problemsFor = (index: number): string[] =>
    problems.filter((problem) => problem.path.startsWith(`$[${String(index)}]`)).map((p) => p.message);
  const documentProblems = problems.filter((problem) => problem.path === '$').map((p) => p.message);

  const replace = (index: number, block: Block): void => {
    const blocks = [...value.blocks];
    blocks[index] = block;
    onChange({ blocks });
  };
  const move = (index: number, by: number): void => {
    const blocks = [...value.blocks];
    const target = index + by;
    if (target < 0 || target >= blocks.length) return;
    const [moved] = blocks.splice(index, 1);
    if (moved) blocks.splice(target, 0, moved);
    onChange({ blocks });
  };

  const selectedIndex = value.blocks.findIndex((block) => block.id === selected);
  const selectedBlock = selectedIndex >= 0 ? value.blocks[selectedIndex] : null;

  return (
    <div {...passthrough(rest)} data-component="BlockEditor" data-palette={palette.key} className="flex w-full min-w-0 gap-4">
      {/* Left: what this surface can carry. */}
      <aside aria-label={labels.palette} data-pane="palette" className="flex w-panel-narrow shrink-0 flex-col gap-1">
        <SectionLabel>{labels.palette}</SectionLabel>
        {palette.allowed.map((type) => (
          <Button
            key={type}
            intent="secondary"
            size="small"
            data-add-block={type}
            disabled={readOnly === true}
            onClick={() => onChange({ blocks: [...value.blocks, newBlock(type)] })}
          >
            {labels.blockType[type]}
          </Button>
        ))}
        {library && library.snippets.length + library.templates.length > 0 ? (
          <div data-block-library className="mt-2 flex flex-col gap-1">
            <SectionLabel>{library.label}</SectionLabel>
            {library.templates.map((template, index) => (
              <Button
                key={`t${String(index)}`}
                intent="ghost"
                size="small"
                data-insert-template={template.name}
                disabled={readOnly === true}
                onClick={() => onChange({ blocks: [...value.blocks, ...freshIds(template.blocks)] })}
              >
                {template.name}
              </Button>
            ))}
            {library.snippets.map((snippet, index) => (
              <Button
                key={`s${String(index)}`}
                intent="ghost"
                size="small"
                data-insert-snippet={snippet.name}
                disabled={readOnly === true}
                onClick={() => onChange({ blocks: [...value.blocks, { ...newBlock('text'), text: snippet.body }] })}
              >
                {snippet.name}
              </Button>
            ))}
          </div>
        ) : null}
      </aside>

      {/* Centre: the canvas, which is also the preview. */}
      <section aria-label={labels.canvas} data-pane="canvas" className="flex min-w-0 flex-1 flex-col gap-2">
        <header className="flex flex-wrap items-center gap-2">
          <SectionLabel>{labels.preview}</SectionLabel>
          <span role="group" aria-label={labels.preview} className="flex items-center gap-1">
            {(['desktop', 'mobile', 'dark'] as const).map((option) => (
              <button
                key={option}
                type="button"
                data-preview-mode={option}
                aria-pressed={mode === option}
                onClick={() => setMode(option)}
                className={cx(
                  'inline-flex h-touch items-center rounded-control px-3 text-dense',
                  mode === option ? 'bg-bronze-tint text-bronze-text' : 'text-ink-600 hover:bg-sunken',
                )}
              >
                {labels.previewMode[option]}
              </button>
            ))}
          </span>
          {onTestSend ? (
            <Button intent="secondary" size="small" icon="PaperPlaneTilt" data-test-send onClick={onTestSend}>
              {labels.testSend}
            </Button>
          ) : null}
        </header>

        {/*
         * The personalisation warning.
         *
         * Above the canvas and unmissable, because this is the failure that
         * survives review: the preview looked right, and every recipient
         * without a first name got "Hi ,".
         */}
        {missing.length > 0 ? (
          <div
            data-missing-tokens
            className="flex flex-col gap-0.5 rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text"
          >
            <span className="flex items-center gap-2">
              <Icon name="Warning" size="inline" decorative />
              {labels.missingTokens}
            </span>
            <span className="text-meta">{labels.missingTokensHelp}</span>
            <ul className="flex flex-wrap gap-2">
              {missing.map((token) => (
                <li key={token} data-missing-token={token} className="rounded-chip bg-surface px-2 text-meta">
                  {`{{${token}}}`}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {documentProblems.length > 0 ? (
          <ul data-document-problems className="flex flex-col gap-0.5 rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text">
            <li className="text-micro uppercase tracking-label">{labels.problems}</li>
            {documentProblems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        ) : null}

        <div
          data-preview-surface={mode}
          className={cx(
            'flex w-full flex-col gap-2 rounded-card border border-rule p-3',
            // Dark preview swaps the ground under the content only. The
            // editor's own chrome stays where it was, so the person is
            // previewing the email rather than changing theme.
            mode === 'dark' ? 'bg-ink text-canvas' : 'bg-surface text-ink',
            mode === 'mobile' ? 'max-w-panel-wide' : palette.canvasWidth === 'reading' ? 'max-w-reading' : 'w-full',
          )}
        >
          {value.blocks.length === 0 ? (
            <p data-canvas-empty className="py-6 text-center text-body text-ink-meta">
              {labels.empty}
            </p>
          ) : (
            value.blocks.map((block, index) => {
              const blockProblems = problemsFor(index);
              return (
                <div
                  key={block.id}
                  data-block={block.id}
                  data-block-type={block.type}
                  data-block-invalid={blockProblems.length > 0 ? 'true' : 'false'}
                  className={cx(
                    'rounded-control p-1',
                    // The selected block, shown with an outline rather than a
                    // ring. The ring colour it used to name did not exist, so a
                    // selected block has had no indicator at all. An outline
                    // also costs no layout, where a border would shift
                    // everything the moment somebody clicked.
                    selected === block.id && 'outline outline-2 outline-offset-1 outline-focus',
                    blockProblems.length > 0 && 'border border-mark-danger',
                  )}
                >
                  <button
                    type="button"
                    data-select-block={block.id}
                    aria-label={labels.selectBlock}
                    onClick={() => setSelected(block.id)}
                    className="block w-full text-start"
                  >
                    <BlockPreview block={block} sample={sample} labels={labels} />
                  </button>
                  {/* The problem sits with the block it is about, not in a
                      list at the top that somebody has to map back. */}
                  {blockProblems.map((problem) => (
                    <p key={problem} data-block-problem={block.id} className="pt-1 text-meta text-danger-text">
                      {problem}
                    </p>
                  ))}
                  {readOnly === true ? null : (
                    <span className="flex gap-1 pt-1">
                      <Button intent="ghost" size="small" icon="ArrowUp" data-move-up={block.id} onClick={() => move(index, -1)}>
                        {labels.moveUp}
                      </Button>
                      <Button intent="ghost" size="small" icon="ArrowDown" data-move-down={block.id} onClick={() => move(index, 1)}>
                        {labels.moveDown}
                      </Button>
                      <Button
                        intent="ghost"
                        size="small"
                        icon="Trash"
                        data-remove-block={block.id}
                        onClick={() => onChange({ blocks: value.blocks.filter((b) => b.id !== block.id) })}
                      >
                        {labels.remove}
                      </Button>
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Right: the selected block's settings. */}
      <aside aria-label={labels.settings} data-pane="settings" className="flex w-panel-wide shrink-0 flex-col gap-2">
        <SectionLabel>{labels.settings}</SectionLabel>
        {!selectedBlock ? (
          <p className="text-meta text-ink-meta">{labels.selectBlock}</p>
        ) : (
          <BlockSettings
            block={selectedBlock}
            labels={labels}
            readOnly={readOnly === true}
            images={library?.images ?? []}
            chooseImage={library?.chooseImage ?? ''}
            onChange={(next) => replace(selectedIndex, next)}
          />
        )}
      </aside>
    </div>
  );
}

/**
 * A whole block document as its reader sees it - the editor's own preview,
 * not a second renderer. `live` is for a hosted page: a button with a link is
 * a link there, where in the editor it is only a picture of one (F-072).
 */
export function BlockDocumentView({
  blocks,
  labels,
  live = false,
  ...rest
}: {
  blocks: readonly Block[];
  labels: BlockEditorLabels;
  live?: boolean;
} & Record<string, unknown>): JSX.Element {
  return (
    <div {...rest} data-component="BlockDocumentView" className="flex flex-col gap-3">
      {blocks.map((block) => (
        <BlockPreview key={block.id} block={block} sample={{}} labels={labels} live={live} />
      ))}
    </div>
  );
}

/** One block, as a recipient would see it - tokens filled, gaps visible. */
function BlockPreview({
  block,
  sample,
  labels,
  live = false,
}: {
  block: Block;
  sample: Record<string, string | null | undefined>;
  labels: BlockEditorLabels;
  live?: boolean;
}): JSX.Element {
  const text = renderText(block.text ?? '', sample);
  const align =
    block.align === 'center' ? 'text-center' : block.align === 'end' ? 'text-end' : 'text-start';

  switch (block.type) {
    case 'heading':
      return <p className={cx('text-h2', align)}>{text}</p>;
    case 'quote':
      return <p className={cx('border-s-4 border-s-bronze ps-2 text-lead italic', align)}>{text}</p>;
    case 'divider':
      return <hr className="border-t border-t-rule" />;
    case 'spacer':
      return <span className="block h-6" aria-hidden />;
    case 'image':
      return block.url ? (
        // The alt text is what most recipients read: mail clients block images
        // by default.
        <img src={block.url} alt={block.alt ?? ''} className="max-w-full rounded-card" />
      ) : (
        <span className="flex h-block-xs items-center justify-center rounded-card bg-sunken text-meta text-ink-meta">
          {labels.blockType.image}
        </span>
      );
    case 'video':
      return (
        <span className="flex h-block-xs items-center justify-center gap-2 rounded-card bg-sunken text-meta text-ink-meta">
          <Icon name="Play" size="inline" decorative />
          {block.url || labels.blockType.video}
        </span>
      );
    case 'button':
      if (live && block.url) {
        return (
          <span className={cx('block', align)}>
            <a
              href={block.url}
              className="inline-flex h-btn items-center rounded-control bg-bronze px-btn-x text-body text-bronze-on"
            >
              {text || labels.blockType.button}
            </a>
          </span>
        );
      }
      return (
        <span className={cx('block', align)}>
          <span className="inline-flex h-btn items-center rounded-control bg-bronze px-btn-x text-body text-bronze-on">
            {text || labels.blockType.button}
          </span>
        </span>
      );
    case 'unsubscribe':
      return (
        <p className="text-center text-meta text-ink-meta underline underline-offset-4">
          {text || labels.blockType.unsubscribe}
        </p>
      );
    case 'line-items':
      /*
       * A placeholder in the editor, the real table in the document.
       *
       * The editor has no record to draw lines from, and inventing sample
       * lines would make a template look finished that has never met a real
       * quote. It says what will be there instead.
       */
      return (
        <span
          data-line-items-placeholder
          className="flex h-block-xs items-center justify-center rounded-card bg-sunken text-meta text-ink-meta"
        >
          {labels.blockType['line-items']}
        </span>
      );
    case 'columns':
      return (
        <span className="flex flex-wrap gap-2">
          {(block.children ?? []).map((column, index) => (
            <span key={index} className="flex min-w-0 flex-1 flex-col gap-1">
              {column.map((child) => (
                <BlockPreview key={child.id} block={child} sample={sample} labels={labels} live={live} />
              ))}
            </span>
          ))}
        </span>
      );
    default:
      return <p className={cx('text-body', align)}>{text}</p>;
  }
}

function BlockSettings({
  block,
  labels,
  readOnly,
  images,
  chooseImage,
  onChange,
}: {
  block: Block;
  labels: BlockEditorLabels;
  readOnly: boolean;
  images: Array<{ name: string; url: string }>;
  chooseImage: string;
  onChange: (next: Block) => void;
}): JSX.Element {
  const field = (
    key: 'text' | 'url' | 'alt' | 'href',
    label: string,
    multiline = false,
  ): JSX.Element => (
    <label className="flex flex-col gap-1 text-dense text-ink">
      {label}
      {multiline ? (
        <textarea
          data-block-field={key}
          aria-label={label}
          disabled={readOnly}
          value={block[key] ?? ''}
          onChange={(event) => onChange({ ...block, [key]: event.target.value })}
          className="rounded-control border border-rule bg-surface px-2 py-1 text-dense text-ink"
        />
      ) : (
        <input
          data-block-field={key}
          aria-label={label}
          disabled={readOnly}
          value={block[key] ?? ''}
          onChange={(event) => onChange({ ...block, [key]: event.target.value })}
          className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
        />
      )}
    </label>
  );

  return (
    <div data-block-settings={block.id} className="flex flex-col gap-2">
      {['text', 'heading', 'quote', 'button', 'unsubscribe'].includes(block.type)
        ? field('text', labels.text, block.type === 'text' || block.type === 'quote')
        : null}
      {block.type === 'image' || block.type === 'video' ? field('url', labels.url) : null}
      {block.type === 'image' && images.length > 0 ? (
        <label className="flex flex-col gap-1 text-dense text-ink">
          {chooseImage}
          <select
            data-block-image-choice
            aria-label={chooseImage}
            disabled={readOnly}
            value={images.some((image) => image.url === block.url) ? block.url : ''}
            onChange={(event) => {
              const chosen = images.find((image) => image.url === event.target.value);
              if (chosen) onChange({ ...block, url: chosen.url, alt: block.alt || chosen.name });
            }}
            className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
          >
            <option value="">-</option>
            {images.map((image) => (
              <option key={image.url} value={image.url}>
                {image.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {block.type === 'image' ? field('alt', labels.alt) : null}
      {block.type === 'button' ? field('href', labels.href) : null}
      {['divider', 'spacer', 'columns', 'line-items'].includes(block.type) ? null : (
        <label className="flex flex-col gap-1 text-dense text-ink">
          {labels.align}
          <select
            data-block-field="align"
            aria-label={labels.align}
            disabled={readOnly}
            value={block.align ?? 'start'}
            onChange={(event) => onChange({ ...block, align: event.target.value as Block['align'] })}
            className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
          >
            {(['start', 'center', 'end'] as const).map((option) => (
              <option key={option} value={option}>
                {labels.alignOptions[option]}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

export { BLOCK_PALETTES };
